import React from "react";
import SkeletonElement from "Components/GMS/Category/Shimmer/SkeletonElement";
import Alert from "Components/DesignSystem/Alert/Alert";
import Spinner from "Components/DesignSystem/Spinner/Spinner";

/**
 * The attribute module's status strip on DS Alert + Spinner — the same § 99
 * grammar as the category module's copy, with one module-specific difference:
 * `Loading` renders the detail pane's full skeleton card (this component is
 * what AttributeDetails shows while a detail fetch is in flight), not a strip.
 *
 * ⚠ Non-urgent strips are polite `role="status"` now; only Error is
 * `role="alert"` (legacy put role="alert" on all of them).
 *
 * `data-testid` comes from the CALLER — several panes render this component,
 * and a baked-in id would duplicate (the § 4 one-testid-one-element rule).
 */
type MyProps = {
    loading: {status?: string, message?: string},
    'data-testid'?: string,
}

const LoadingSuccessErrorAlert = ({ loading, "data-testid": dataTestId }: MyProps) => {
    return (
      <>
        {loading.status === "Saving" && (
          <Alert tone="info" live data-testid={dataTestId}>
            <span className="tw-inline-flex tw-items-center tw-gap-2">
              <Spinner size="sm" aria-hidden="true" />
              <span>{loading.message}</span>
            </span>
          </Alert>
        )}
        {loading.status === "Loading" && (
          <div className="d-flex w-100 h-100 ms-4 ">
            <div className="row w-100 mt-0 mb-3">
              <div className="col col-md-12">
                <div className="p-3 tw-border tw-border-edge tw-rounded-ctl tw-bg-surface">
                  <div className="d-flex justify-content-between ps-1">
                    <SkeletonElement type={"title"} />
                    <div className="d-flex">
                      <SkeletonElement type={"title25"} />
                      &nbsp;
                      <div className="d-flex justify-content-center align-items-center">
                        <SkeletonElement type={"title25"} />
                      </div>
                    </div>
                  </div>
                  <div className="ps-1 w-25">
                    <SkeletonElement type={"text"} />
                    <SkeletonElement type={"text"} />
                  </div>
                </div>
                <br />
                <SkeletonElement type={"title"} />
                <br />
                <SkeletonElement type={"title"} />
                <br />
                <SkeletonElement type={"title"} />
                <br />
                <SkeletonElement type={"title"} />
                <br />
              </div>
            </div>
          </div>
        )}
        {loading.status === "Error" && (
          <Alert tone="danger" live data-testid={dataTestId}>
            {loading.message}
          </Alert>
        )}
        {loading.status === "Success" && loading.message !== undefined && (
          <Alert tone="success" live data-testid={dataTestId}>
            {loading.message}
          </Alert>
        )}
      </>
    );
  };

export default LoadingSuccessErrorAlert
